import { Injectable, untracked } from '@angular/core';
import { MapsSate } from "../core/states/maps.state";
import { MarkerGoogleMaps } from "../shared/models/maps/markerGoogleMaps";
import { Observable, firstValueFrom } from 'rxjs';
import { MapsService } from '../shared/services/maps.service';
import { TypeMapMarker } from '../core/enums/typeMapMarker';
import isEqual from 'lodash/isEqual';


@Injectable({
    providedIn: 'root',
})
export class MapsFacade {

    constructor(
        private mapsState: MapsSate,
        private mapsService: MapsService
    ) { }

    getMarkers$(): Observable<MarkerGoogleMaps[]> {
        return this.mapsState.getMarkers$();
    }

    getMarkers(): MarkerGoogleMaps[] {
        return this.mapsState.getMarkers();
    }

    addMarker(marker: MarkerGoogleMaps) {
        this.mapsState.addMarker(marker);
    }

    setMarkers(markers: MarkerGoogleMaps[]) {
        this.mapsState.setMarkers(markers);
    }

    mergeMarkers(markers: MarkerGoogleMaps[]) {
        const previous = this.mapsState.getMarkers() || [];
        const combined = [...previous, ...markers];
        const uniqueMarkers: MarkerGoogleMaps[] = []; //without duplicates

        for (const m of combined) {
            const exists = uniqueMarkers.some(x => isEqual(x, m));
            if (!exists) {
            uniqueMarkers.push(m);
            }
        }
        this.mapsState.setMarkers(uniqueMarkers);
        }

    setVisibleMarker(markerId: number, value:boolean) {
        this.mapsState.setVisibleMarker(markerId,value)
    }

    setVisibleAllMarkers(value:boolean) {
        this.mapsState.setVisibleAllMarkers(value)
    }

    addClusters() {
        const markers = this.getMarkers();
        const previousMembers: MarkerGoogleMaps[] = this.getClustersMembers() || [];
        // const {
        //     updatedMarkers,
        //     clusters,
        //     clustersMembers
        //   } = this.mapsService.filterAndCluster(markers, previousMembers);
        const result = this.mapsService.filterAndCluster(markers, previousMembers);
        !!result?.clustersMembers ?  this.setAllClusterMembers(result.clustersMembers) : null;
        !!result?.updatedMarkers ?  this.setMarkers(result.updatedMarkers) : null;
        !!result?.clusters ?  this.mergeMarkers(result.clusters) : null;
      }

    //only visibleTypes set visibility to true
    public setProvidersMarkersVisibility(visibleTypes: TypeMapMarker[]): void {
        const state = this.mapsState.getMarkers();
        let update = [];
       if(!!state && state.length > 0){
         update = state.map(marker => ({
          ...marker,
          visible: visibleTypes.includes(marker.type)
        }));
        this.mapsState.setMarkers(update);
    }
        let stateClustersMembers = this.mapsState.getClustersMembers();
        let updated = [];
    if(!!stateClustersMembers && stateClustersMembers.length > 0){
        updated = stateClustersMembers.map(marker => ({
        ...marker,
        visible: visibleTypes.includes(marker.type)
        }));
        this.mapsState.setMarkersClustered(updated);
    }
      }

      public async deleteMarkersByType(type: TypeMapMarker) {
        const getMarkers = this.mapsState.getMarkers();
        const filteredMarkers = getMarkers?.filter(marker => marker.type != type)
        this.mapsState.setMarkers(filteredMarkers);
    }

    public async deleteMarkersByTypes(types: TypeMapMarker[]): Promise<void> {
        const getMarkers = this.mapsState.getMarkers();
        const typesSet = new Set(types.map(t => String(t)));

        const filteredMarkers = getMarkers?.filter(marker => {
            const markerType = String(marker.type);
            return !typesSet.has(markerType);
        });

        if (!filteredMarkers || filteredMarkers.length === 0) {
            this.mapsState.setMarkers([]);
        } else {
            this.mapsState.setMarkers(filteredMarkers);
        }
        this.deleteClusters(types);
    }

    public setClusterVisibility(clusterId: number, visible: boolean): void {
       const markers = this.getMarkers();
         const updatedMarkers = markers.map(marker => {
                if (marker.clusterId === clusterId) {
                 return { ...marker, visible: visible };
                }
                return marker;
          });
          this.mapsState.setMarkers(updatedMarkers);
    }

    public setClusterMembersVisibility (value:boolean){
        const members = this.getClustersMembers();
        members.forEach(m => {
            m.visible = value});
        this.mapsState.setMarkersClustered(members);
    }

    public deleteAllMarkers(): void {
            let state = this.mapsState.getMarkers()
            let result: MarkerGoogleMaps[] = [];
            if(!!state && state.length > 0){
            result = state.filter(marker => {
                return marker.type == TypeMapMarker.GENERAL;
            });
        }
        this.mapsState.setMarkers(result);
    }

    public setAllClusterMembers(members: MarkerGoogleMaps[]): void {
        this.mapsState.setMarkersClustered(members);
    }

    public async deleteAllClusterMembers(): Promise<void> {
        this.mapsState.setMarkersClustered([]);
    }

    public setClusterMemberVisibility(id: number,value:boolean): void {
        const members = this.getClustersMembers();
        const target = members.find(m => m.id === id);
        if (!target) {
            console.warn(`No existe ningún miembro con id ${id}`);
            return;
        }
        const clusterId = target.clusterId;
        const clusterMembers = members.filter(m => m.clusterId === clusterId);
        const visibleBeforeCount = clusterMembers.filter(m => m.visible).length;
        target.visible = value;
        this.setAllClusterMembers(members);

        const visibleAfterCount = clusterMembers.filter(m => m.visible).length;
        this.handleClusterVisibilityChange(clusterId, target.visible, visibleBeforeCount, visibleAfterCount);
    }

    public setAllClusterMemberVisibility(value:boolean): void {
        const members = this.getClustersMembers();
        members.forEach(m => {
            m.visible = value});
        this.setAllClusterMembers(members);

    }

    private handleClusterVisibilityChange( clusterId: number,isNowVisible: boolean,visibleBefore: number, visibleAfter: number): void {
        // If  the last member was hidden, hide the cluster
    if (!isNowVisible && visibleAfter === 0) {
        this.setClusterVisibility(clusterId, false);
    }
    // if  the first member was shown, show the cluster
    else if (isNowVisible && visibleBefore === 0) {
        this.setClusterVisibility(clusterId, true);
    }
    }

    public getAllClusterMembers$(): Observable<MarkerGoogleMaps[]> {
        return this.mapsState.getMarkersClustered$();
    }

    public getClustersMembers():MarkerGoogleMaps[]{
        return this.mapsState.getClustersMembers();
    }



public async deleteClusters(types: TypeMapMarker[]): Promise<void> {
    this.deleteClusterMermbers(types); //update members
    const remainingCounts = this.countAllMembersByClusterId();
    const allMarkers = untracked(() => this.mapsState.getMarkers() ?? []);
    const updated = allMarkers
      .map(m => m.type === TypeMapMarker.CLUSTER
        ? { ...m, clusterSize: remainingCounts[m.id] ?? 0 }
        : m
      )
      .filter(m => m.type !== TypeMapMarker.CLUSTER || (m.clusterSize ?? 0) > 0);

    this.mapsState.setMarkers(updated);
  }

  private deleteClusterMermbers(types: TypeMapMarker[]): void { //on touch pois zone or pois address visibility
    const members = this.getClustersMembers();
    const filteredMembers = members.filter(m => !types.includes(m.type));
    this.setAllClusterMembers(filteredMembers);
  }

  private countAllMembersByClusterId(): Record<number, number> {
    const members = this.getClustersMembers();
    return members.reduce((acc, m) => {
      if (m.clusterId != null) {
        acc[m.clusterId] = (acc[m.clusterId] || 0) + 1;
      }
      return acc;
    }, {} as Record<number, number>);

}
};
